import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { TK } from '../store/translations/translationKeys';
import { useTranslations } from '../store/translations/hooks';
import { ConsentManagementSettingsData, ClientFile, Consents } from '../models';
import { CheckInputField } from './Forms/fields/CheckInputField';
import { getConsentGroupName, getConsentLabel } from '../shared/consentUtils';
import { uploadPhysicalConsentAsync } from '../ajax/requests';
import { useDispatch } from 'react-redux';
import { alertError, alertSuccess } from '../store/alerts/actions';
import { getErrorMessage } from '../ajax/errors';
import AppButton from './AppButton';

interface UploadConsentDocumentModalProps {
    clientId: string;
    show: boolean;
    close: () => void;
    consentSettings: ConsentManagementSettingsData;
    onSuccess: (uploadedFile: ClientFile) => void;
}

const emptyConsents = (): Consents => ({
    gdpr: false,
    healthDataProcessing: false,
    emailMarketing: false,
    smsMarketing: false,
    birthdayMessage: false,
    emailReminders: false,
    smsReminders: false,
    whatsAppReminders: false,
    dataSharingWithProfessionals: false,
    thirdPartyDataSharing: false,
    imageOrVideoCaptureForInstitutionalPurposes: false,
});

const UploadConsentDocumentModal: React.FC<UploadConsentDocumentModalProps> = ({
    clientId,
    show,
    close,
    consentSettings,
    onSuccess,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();

    const [approvedConsents, setApprovedConsents] = React.useState<Consents>(emptyConsents);
    const [customCheckboxStates, setCustomCheckboxStates] = React.useState<Record<string, boolean>>({});
    const [file, setFile] = React.useState<File | null>(null);
    const [uploading, setUploading] = React.useState(false);

    React.useEffect(() => {
        if (show) {
            setApprovedConsents(emptyConsents());
            setCustomCheckboxStates({});
            setFile(null);
        }
    }, [show]);

    const atLeastOneChecked = Object.values(approvedConsents).some(v => v)
        || Object.values(customCheckboxStates).some(v => v);
    const canSubmit = atLeastOneChecked && file !== null;

    const handleConsentToggle = (key: keyof Consents, value: boolean) => {
        setApprovedConsents(prev => ({ ...prev, [key]: value }));
    };

    const handleCustomCheckboxToggle = (text: string, value: boolean) => {
        setCustomCheckboxStates(prev => ({ ...prev, [text]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0] ?? null);
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setUploading(true);
        try {
            const approvedCustomCheckboxTexts = Object.entries(customCheckboxStates)
                .filter(([, v]) => v)
                .map(([text]) => text);
            const uploaded = await uploadPhysicalConsentAsync(clientId, file!, approvedConsents, approvedCustomCheckboxTexts);
            dispatch(alertSuccess(t(TK.consentPhysicalUploadSuccess)));
            onSuccess(uploaded);
            close();
        } catch (err: unknown) {
            dispatch(alertError(getErrorMessage(err) || t(TK.somethingWentWrong)));
        } finally {
            setUploading(false);
        }
    };

    const checkboxGroups = consentSettings?.consentsCheckboxes ?? [];

    return (
        <Modal isOpen={show} toggle={close} size="lg">
            <ModalHeader toggle={close}>{t(TK.uploadPhysicalConsent)}</ModalHeader>
            <ModalBody>
                <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacer-M)' }}>
                    {t(TK.uploadPhysicalConsentDescription)}
                </p>

                <p style={{ fontWeight: 600, marginBottom: 'var(--spacer-S)' }}>
                    {t(TK.markConsentApprovals)}
                </p>

                {checkboxGroups.map((group, groupIndex) => {
                    const enabledConsents = (Object.entries(group.consents) as [keyof Consents, boolean][])
                        .filter(([, enabled]) => enabled)
                        .map(([key]) => key);
                    const isCustom = enabledConsents.length === 0;

                    if (isCustom && !group.text) return null;

                    return (
                        <div key={groupIndex} style={{ marginBottom: 'var(--spacer-M)' }}>
                            {isCustom ? (
                                <CheckInputField
                                    label={group.text}
                                    value={customCheckboxStates[group.text] ?? false}
                                    onChange={value => handleCustomCheckboxToggle(group.text, value)}
                                />
                            ) : (
                                <>
                                    <p style={{
                                        fontWeight: 600,
                                        fontSize: '0.9em',
                                        color: 'var(--text-muted)',
                                        marginBottom: 'var(--spacer-XS)',
                                        textTransform: 'uppercase',
                                        borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                                        paddingBottom: 'var(--spacer-XXS)'
                                    }}>
                                        {getConsentGroupName(group.name ?? '', t)}
                                    </p>
                                    {enabledConsents.map(key => (
                                        <CheckInputField
                                            key={key}
                                            label={getConsentLabel(key, t)}
                                            value={approvedConsents[key]}
                                            onChange={value => handleConsentToggle(key, value)}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    );
                })}

                <div style={{ marginTop: 'var(--spacer-M)', borderTop: 'var(--border-thin) solid var(--appLigthGrey)', paddingTop: 'var(--spacer-M)' }}>
                    <label style={{ fontWeight: 600, marginBottom: 'var(--spacer-XS)', display: 'block' }}>
                        {t(TK.uploadPhysicalConsentFile)}
                    </label>
                    <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={close} disabled={uploading}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton color="primary" onClick={handleSubmit} disabled={!canSubmit || uploading}>
                    {uploading ? <Spinner size="sm" /> : t(TK.uploadFile)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default UploadConsentDocumentModal;
